import { useCallback, useState } from 'react';
import { PitColumn } from '../types';
import { PIT_COLUMNS } from './pitBoardStore';

/**
 * Left-to-right order of the pit board's colour columns.
 *
 * Remembered per device, not on the shared board: the pit wall may want the
 * lane nearest the exit on the right while a laptop keeps the default.
 */
const STORAGE_KEY = 'karting-pit-column-order';

/** Only a full permutation of the three columns counts; anything else is ignored. */
export function parseColumnOrder(raw: string | null): PitColumn[] | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== PIT_COLUMNS.length) return null;
    const isPermutation = PIT_COLUMNS.every(c => parsed.includes(c));
    return isPermutation ? (parsed as PitColumn[]) : null;
  } catch {
    return null;
  }
}

export function storedColumnOrder(): PitColumn[] {
  try {
    return parseColumnOrder(localStorage.getItem(STORAGE_KEY)) ?? [...PIT_COLUMNS];
  } catch {
    // Private mode throws rather than returning null.
    return [...PIT_COLUMNS];
  }
}

/** Swaps the column with its neighbour; a no-op at either end. */
export function shiftColumn(
  order: PitColumn[],
  column: PitColumn,
  direction: -1 | 1,
): PitColumn[] {
  const from = order.indexOf(column);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= order.length) return order;
  const next = [...order];
  next[from] = order[to];
  next[to] = order[from];
  return next;
}

export function useColumnOrder(): {
  order: PitColumn[];
  move: (column: PitColumn, direction: -1 | 1) => void;
} {
  const [order, setOrder] = useState<PitColumn[]>(storedColumnOrder);

  const move = useCallback((column: PitColumn, direction: -1 | 1) => {
    setOrder(current => {
      const next = shiftColumn(current, column, direction);
      if (next === current) return current;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Not being able to remember it is no reason to refuse the move.
      }
      return next;
    });
  }, []);

  return { order, move };
}
